import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Grid, Paper, Tab, Tabs, Button } from '@mui/material';
import { MdAdd } from 'react-icons/md';
import { useSnackbar } from 'notistack';
import { AuthContext } from '../context/AuthProvider';
import {
  getAllEngagementsApi,
  getAllFavourites,
  addEngagementToFavourite,
  removeEngagementFromFavourite,
} from "../api's/EngagementApi";
import { getAllClientsApi } from "../api's/ClientApi";
import { ENGAGEMENT_PAGE, CLIENT_PAGE } from '../utils/commonConstants';
import AllEngagementsTable from '../components/Engagements/AllEngagementsTable';
import ClientNoData from '../components/Client/ClientNoData';
import AddEngagementModal from '../reusable-component/AddEngagementModal';
import AccessModal from '../reusable-component/AccessModal';
import SearchAndFilter from '../reusable-component/SearchAndFilter';
import LoadingIcon from '../reusable-component/LoadingIcon';

const EngagementPage = () => {
  const rowsPerPage = 5;
  const { enqueueSnackbar } = useSnackbar();
  const { userRole } = useContext(AuthContext);

  const [tabValue, setTabValue] = useState(() => {
    const storedTab = sessionStorage.getItem('engagement_tab_value');
    return storedTab !== null ? parseInt(storedTab, 10) : 0;
  });
  const [search, setSearch] = useState(sessionStorage.getItem('filtered_engagement_search') || '');
  const [client, setClient] = useState(sessionStorage.getItem('filtered_engagement_client') || '');
  const [status, setStatus] = useState(sessionStorage.getItem('filtered_engagement_status') || '');
  const [page, setPage] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [favouriteEngagements, setFavouriteEngagements] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openAccessModal, setOpenAccessModal] = useState(false);
  const [clientList, setClientList] = useState([]);
  const debounceTimer = useRef(null);
  const isTabInitialized = useRef(false);

  const fetchClientList = async () => {
    try {
      const res = await getAllClientsApi();
      if (res.status === 200) {
        const uniqueClients = [...new Set(res.data.map((item) => item.client_name))];
        setClientList(uniqueClients);
      }
    } catch (err) {
      enqueueSnackbar('Failed to fetch client list', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  };

  const fetchData = useCallback(
    async (pageNum = 0, searchValue = search, clientValue = client, statusValue = status) => {
      setLoading(true);
      try {
        const payload = {
          page: pageNum,
          size: rowsPerPage,
          engagementName: searchValue || null,
          clientName: clientValue || null,
          ...(tabValue === 0 && { status: statusValue?.toLowerCase() || null }),
        };

        const response = tabValue === 0 ? await getAllEngagementsApi(payload) : await getAllFavourites(payload);

        if (response.status === 200) {
          const { engagementData = [], totalData = 0 } = response.data;
          tabValue === 0 ? setTableData(engagementData) : setFavouriteEngagements(engagementData);
          setTotalCount(totalData);
        }
      } catch (err) {
        enqueueSnackbar('Failed to load engagements', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        setTableData([]);
        setFavouriteEngagements([]);
      } finally {
        setLoading(false);
      }
    },
    [tabValue, search, client, status]
  );

  const debouncedFetch = useCallback(
    (pageNum, s, c, st) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchData(pageNum, s, c, st);
      }, 500);
    },
    [fetchData]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    sessionStorage.setItem('filtered_engagement_search', value);
    setSearch(value);
    setPage(0);
    debouncedFetch(0, value, client, status);
  };

  const handleClientChange = (e) => {
    const value = e.target.value;
    sessionStorage.setItem('filtered_engagement_client', value);
    setClient(value);
    setPage(0);
    fetchData(0, search, value, status);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    sessionStorage.setItem('filtered_engagement_status', value);
    setStatus(value);
    setPage(0);
    fetchData(0, search, client, value);
  };

  const handleClearFilter = () => {
    const isAnyFilterApplied = search || client || status;
    if (!isAnyFilterApplied) return;
    sessionStorage.removeItem('filtered_engagement_search');
    sessionStorage.removeItem('filtered_engagement_client');
    sessionStorage.removeItem('filtered_engagement_status');
    setSearch('');
    setClient('');
    setStatus('');
    setPage(0);
    fetchData(0, '', '', '');
  };

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    sessionStorage.setItem('engagement_tab_value', newValue);
    setPage(0);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    fetchData(newPage, search, client, status);
  };
  const handleSearch = () => {
    setPage(0);
    fetchData(0, search, client, status);
  };

  const toggleFavoriteEngagement = async (id) => {
    try {
      const engagementList = tabValue === 0 ? tableData : favouriteEngagements;
      const engagement = engagementList.find((e) => e.engagement_id === id);
      const isFav = engagement?.is_fav || false;
      const apiCall = isFav ? removeEngagementFromFavourite : addEngagementToFavourite;

      const response = await apiCall(id);
      if (response.status === 200) {
        enqueueSnackbar(isFav ? 'Removed from favorites' : 'Added to favorites', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        fetchData(page, search, client, status);
      }
    } catch {
      enqueueSnackbar('Failed to update favorites', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  };

  const handleOpenModal = () => {
    if (userRole === 'READER' || userRole === 'EXECUTOR') {
      setOpenAccessModal(true);
    } else {
      setOpenModal(true);
    }
  };

  useEffect(() => {
    fetchClientList();
    fetchData(0, search, client, status);
  }, []);

  useEffect(() => {
    if (isTabInitialized.current) {
      fetchData(0, search, client, status);
    }
  }, [tabValue]);
  useEffect(() => {
    const storedTab = sessionStorage.getItem('engagement_tab_value');
    if (storedTab !== null) {
      const parsedTab = parseInt(storedTab, 10);
      setTabValue(parsedTab);
    }
    isTabInitialized.current = true;
  }, []);

  const filterData = {
    search,
    status,
    region: client,
    handleSearch,
    handleOnChange: handleSearchChange,
    handleStatus: handleStatusChange,
    handleRegion: handleClientChange,
    handleClearFilter,
    firstFilterText: 'Filter By Client',
    secondFilterText: 'Filter By Status',
    clearFilterText: CLIENT_PAGE.clientClearFilterButton,
    firstFilterValues: {
      value: client,
      filterData: clientList,
    },
    secondFilterValues: {
      value: status,
      filterData: ['Approved', 'Rejected', 'Pending'],
    },
    isFirstFilterVisible: true,
    isSecondFilterVisible: tabValue === 0,
    isThirdFilterVisible: false,
  };

  return (
    <Box>
      <Grid container justifyContent="space-between">
        <Grid item>{ENGAGEMENT_PAGE.engagementPageTitle}</Grid>
        <Grid item>
          <Button variant="contained" sx={{ backgroundColor: '#00043C' }} onClick={handleOpenModal}>
            <MdAdd />
            {ENGAGEMENT_PAGE.addNewEngagementButton}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: '#47D7AC' } }}
          sx={{ '& .MuiTab-root.Mui-selected': { color: '#00043C' } }}
        >
          <Tab label="All" sx={{ fontWeight: 'bold' }} />
          <Tab label="Favourite" sx={{ fontWeight: 'bold' }} />
        </Tabs>
      </Box>

      <SearchAndFilter data={filterData} />

      <Box mt={2}>
        {loading ? (
          <LoadingIcon />
        ) : (tabValue === 0 ? tableData : favouriteEngagements).length > 0 ? (
          <AllEngagementsTable
            tableData={tabValue === 0 ? tableData : favouriteEngagements}
            fetchAllEngagementsTableData={fetchData}
            currentPage={page}
            search={search}
            setPage={setPage}
            totalCount={totalCount}
            favouriteEngagements={favouriteEngagements}
            toggleFavoriteEngagement={toggleFavoriteEngagement}
            handleChangePage={handleChangePage}
          />
        ) : (
          <Paper elevation={3} sx={{ height: 350 }}>
            <ClientNoData
              title={ENGAGEMENT_PAGE.engagementPageNoDataToDisplay}
              text={
                tabValue === 0
                  ? ENGAGEMENT_PAGE.noEngagementToReview
                  : 'You have no favourite engagements. Browse all engagements and mark them as favourite.'
              }
              addBtnText={tabValue === 1 ? 'Browse Engagements' : undefined}
              handleOnClick={tabValue === 1 ? () => setTabValue(0) : undefined}
              isAddBtn={tabValue === 1}
            />
          </Paper>
        )}
      </Box>

      <AddEngagementModal openModal={openModal} handleCloseModal={() => setOpenModal(false)} fetchData={fetchData} />
      <AccessModal openAccessModal={openAccessModal} handleAccessModal={() => setOpenAccessModal(false)} />
    </Box>
  );
};

export default EngagementPage;
